import { getYear, lightFormat, parseISO, subYears } from 'date-fns';

import { FIELD_KEYS } from '_constants/forms';

const marriedFilingSeparatelyValues = {
  separately: true,
  jointly: false,
};

const filingSingleValues = {
  household: false,
  single: true,
};

export const createTaxReportsBody = (form = {}, taxReports = {}) => {
  const yearTagKey = getYear(subYears(new Date(), 1));
  const currentTaxReport = taxReports[yearTagKey];

  const individual = {
    [FIELD_KEYS.firstName]: form[FIELD_KEYS.firstName],
    [FIELD_KEYS.lastName]: form[FIELD_KEYS.lastName],
    [FIELD_KEYS.ssn]: form[FIELD_KEYS.ssn],
    [FIELD_KEYS.dateOfBirth]: lightFormat(form[FIELD_KEYS.dateOfBirth], 'yyyy-MM-dd'),
    [FIELD_KEYS.email]: form[FIELD_KEYS.email],
    [FIELD_KEYS.phone]: form[FIELD_KEYS.phone]?.replace(/\D/g, ''),
    countryOfCitizenship: null,
    suffixName: null,
    address: {
      [FIELD_KEYS.address1]: form[FIELD_KEYS.address1],
      [FIELD_KEYS.zip]: form[FIELD_KEYS.zip],
      [FIELD_KEYS.city]: form[FIELD_KEYS.city],
      [FIELD_KEYS.state]: form[FIELD_KEYS.state],
      country: 'US',
      county: null,
      permanent: false,
    },
  };

  const dependents = [];

  form[FIELD_KEYS.dependents]?.forEach((dependent) => {
    dependents.push({
      [FIELD_KEYS.relationship]: dependent[FIELD_KEYS.relationship],
      [FIELD_KEYS.firstName]: dependent[FIELD_KEYS.firstName],
      [FIELD_KEYS.lastName]: dependent[FIELD_KEYS.lastName],
      [FIELD_KEYS.ssn]: dependent[FIELD_KEYS.ssn],
      [FIELD_KEYS.dateOfBirth]: lightFormat(dependent[FIELD_KEYS.dateOfBirth], 'yyyy-MM-dd'),
      [FIELD_KEYS.email]: dependent[FIELD_KEYS.email],
      [FIELD_KEYS.phone]: dependent[FIELD_KEYS.phone]?.replace(/\D/g, ''),
      countryOfCitizenship: null,
      suffixName: null,
      address: {
        [FIELD_KEYS.address1]: dependent[FIELD_KEYS.address1],
        [FIELD_KEYS.zip]: dependent[FIELD_KEYS.zip],
        [FIELD_KEYS.city]: dependent[FIELD_KEYS.city],
        [FIELD_KEYS.state]: dependent[FIELD_KEYS.state],
        country: 'US',
        county: null,
        permanent: false,
      },
    });
  });

  let spouse = null;

  if (form[FIELD_KEYS.spouse] === 'yes') {
    spouse = {
      [FIELD_KEYS.firstName]: form[FIELD_KEYS.spouseFirstName],
      [FIELD_KEYS.lastName]: form[FIELD_KEYS.spouseLastName],
      [FIELD_KEYS.ssn]: form[FIELD_KEYS.spouseSsn],
      [FIELD_KEYS.dateOfBirth]: lightFormat(form[FIELD_KEYS.spouseDateOfBirth], 'yyyy-MM-dd'),
      [FIELD_KEYS.email]: form[FIELD_KEYS.spouseEmail],
      [FIELD_KEYS.phone]: form[FIELD_KEYS.spousePhone]?.replace(/\D/g, ''),
      countryOfCitizenship: null,
      suffixName: null,
      address: {
        [FIELD_KEYS.address1]: form[FIELD_KEYS.spouseAddress1],
        [FIELD_KEYS.zip]: form[FIELD_KEYS.spouseZip],
        [FIELD_KEYS.city]: form[FIELD_KEYS.spouseCity],
        [FIELD_KEYS.state]: form[FIELD_KEYS.spouseState],
        country: 'US',
        county: null,
        permanent: false,
      },
    };
  }

  return {
    [yearTagKey]: {
      individual,
      dependents,
      spouse,
      [FIELD_KEYS.status]: currentTaxReport?.status || null,
      [FIELD_KEYS.marriedFilingSeparately]:
        marriedFilingSeparatelyValues[form[FIELD_KEYS.marriedFilingSeparately]] ?? null,
      [FIELD_KEYS.filingSingle]: filingSingleValues[form[FIELD_KEYS.filingSingle]] ?? null,
    },
  };
};

export const parseTaxReports = (taxReports = {}) => {
  const tags = Object.keys(taxReports);
  const currentTag = [...tags].sort((a, b) => a - b)[tags.length - 1];
  const reports = {};

  tags.forEach((tag) => {
    const {
      individual: _individual,
      dependents = [],
      spouse: _spouse,
      status,
      filingSingle,
      marriedFilingSeparately,
    } = taxReports[tag];

    const spouse = _spouse || {};
    const individual = _individual || {};
    const individualAddress = individual?.address || {};
    const spouseAddress = spouse.address || {};

    const parsedDependents = dependents?.map((dependent) => {
      const dependentAddress = dependent.address || {};
      return {
        [FIELD_KEYS.relationship]: dependent[FIELD_KEYS.relationship] || '',
        [FIELD_KEYS.firstName]: dependent[FIELD_KEYS.firstName] || '',
        [FIELD_KEYS.lastName]: dependent[FIELD_KEYS.lastName] || '',
        [FIELD_KEYS.ssn]: dependent[FIELD_KEYS.ssn] || '',
        [FIELD_KEYS.dateOfBirth]: dependent[FIELD_KEYS.dateOfBirth]
          ? parseISO(individual[FIELD_KEYS.dateOfBirth])
          : new Date(),
        [FIELD_KEYS.email]: dependent[FIELD_KEYS.email] || '',
        [FIELD_KEYS.phone]: dependent[FIELD_KEYS.phone] || '',
        [FIELD_KEYS.address1]: dependentAddress[FIELD_KEYS.address1] || '',
        [FIELD_KEYS.zip]: dependentAddress[FIELD_KEYS.zip] || '',
        [FIELD_KEYS.city]: dependentAddress[FIELD_KEYS.city] || '',
        [FIELD_KEYS.state]: dependentAddress[FIELD_KEYS.state] || '',
      };
    });

    reports[tag] = {
      [FIELD_KEYS.firstName]: individual[FIELD_KEYS.firstName] || '',
      [FIELD_KEYS.lastName]: individual[FIELD_KEYS.lastName] || '',
      [FIELD_KEYS.ssn]: individual[FIELD_KEYS.ssn] || '',
      [FIELD_KEYS.dateOfBirth]: individual[FIELD_KEYS.dateOfBirth]
        ? parseISO(individual[FIELD_KEYS.dateOfBirth])
        : new Date(),
      [FIELD_KEYS.email]: individual[FIELD_KEYS.email] || '',
      [FIELD_KEYS.phone]: individual[FIELD_KEYS.phone] || '',
      [FIELD_KEYS.address1]: individualAddress[FIELD_KEYS.address1] || '',
      [FIELD_KEYS.zip]: individualAddress[FIELD_KEYS.zip] || '',
      [FIELD_KEYS.city]: individualAddress[FIELD_KEYS.city] || '',
      [FIELD_KEYS.state]: individualAddress[FIELD_KEYS.state] || '',
      [FIELD_KEYS.spouse]: _spouse ? 'yes' : 'no',
      [FIELD_KEYS.spouseFirstName]: spouse[FIELD_KEYS.firstName] || '',
      [FIELD_KEYS.spouseLastName]: spouse[FIELD_KEYS.lastName] || '',
      [FIELD_KEYS.spouseSsn]: spouse[FIELD_KEYS.ssn] || '',
      [FIELD_KEYS.spouseDateOfBirth]: spouse[FIELD_KEYS.dateOfBirth]
        ? parseISO(spouse[FIELD_KEYS.dateOfBirth])
        : new Date(),
      [FIELD_KEYS.spouseEmail]: spouse[FIELD_KEYS.email] || '',
      [FIELD_KEYS.spousePhone]: spouse[FIELD_KEYS.phone] || '',
      [FIELD_KEYS.spouseAddress1]: spouseAddress[FIELD_KEYS.address1] || '',
      [FIELD_KEYS.spouseZip]: spouseAddress[FIELD_KEYS.zip] || '',
      [FIELD_KEYS.spouseCity]: spouseAddress[FIELD_KEYS.city] || '',
      [FIELD_KEYS.spouseState]: spouseAddress[FIELD_KEYS.state] || '',
      [FIELD_KEYS.dependents]: parsedDependents,
      [FIELD_KEYS.status]: status || null,
      [FIELD_KEYS.marriedFilingSeparately]: null,
      [FIELD_KEYS.filingSingle]: 'single',
    };

    if (marriedFilingSeparately !== null) {
      reports[tag].marriedFilingSeparately = marriedFilingSeparately ? 'separately' : 'jointly';
    }

    if (filingSingle !== null) {
      reports[tag].filingSingle = filingSingle ? 'single' : 'household';
    }
  });

  return { currentTag, tags, reports };
};

export const getBusinessTaxFilingProduct = (data) => {
  return data?.products?.find(({ code: { code } }) => {
    return code.toLowerCase().includes('tax_filing') && !code.toLowerCase().includes('personal_tax_filing');
  });
};

export const getPersonalTaxFilingProduct = (data) => {
  return data?.products?.find(({ code: { code } }) => {
    return code.toLowerCase().includes('personal_tax_filing');
  });
};
